export const informProject = {
  id: 2, 
  slug: 'inform',
  title: 'Inform', 
  description: 'AI-powered customer review management.', 
  tags: ['design', 'web application', 'IA'],
  image: '/inform/Inform - Cover.png',
  logo: '/inform/Inform - Logo.png',
  heroLogo: '/inform/Inform - Logo No BG.svg',
  heroImage: '/inform/Inform - Header.png',
  heroDescription: 'A modern platform that helps users manage and share information effectively.',
  headerBackground: '#4E8EED',
  meta: {
    role: ['Founder', 'UI/UX Designer'],
    timeline: '2023 - present',
    tools: 'Figma',
  },
  liveUrl: 'https://informreviews.net',
  overview: {
    title: 'Overview',
    paragraphs: [
      'Inform Reviews is a web platform that helps businesses manage and respond to customer reviews using AI-powered automation and insights.',
    ],
  },
  goal: {
    title: 'Goal',
    description: 'Design a clear, efficient experience that allows users to quickly understand the product\'s value, connect their accounts, and manage reviews with confidence and minimal effort.',
  },
  challenge: {
    title: 'Challenges',
    description: 'Designing a system that can handle complex information structures while remaining accessible and easy to use for all users.',
    problemAreas: [
      'Communicating AI value through visual design',
      'Establishing clear visual hierarchy in a data-heavy interface',
    ],
  },
  problems: [
    {
      id: 'problem-1',
      title: 'Problem 1: Communicating AI Value Through Visual Design',
      whatWasHappening: 'Inform Reviews uses AI to automate review responses and surface sentiment insights—concepts that can feel abstract without clear visual cues. The challenge was to communicate how the product works and where value is delivered through interface design alone, without relying on long explanations or technical language.',
      keyFindings: [
        'Users needed to quickly understand what actions were automated versus user-controlled',
        'Core value (automated responses and insights) was not immediately obvious without visual guidance',
        'Overly descriptive text risked increasing cognitive load rather than improving clarity',
      ],
      whyItMattered: 'Without clear visual communication of AI functionality and value, users struggled to understand the product\'s core benefits, potentially reducing adoption and engagement.',
      designFocus: [
        'Use visual hierarchy to surface primary actions and outcomes',
        'Break complex functionality into clear, digestible sections',
        'Communicate AI behavior through states, previews, and labels',
        'Rely on layout, grouping, and iconography instead of dense copy',
      ],
    },
    {
      id: 'problem-2',
      title: 'Problem 2: Establishing Clear Visual Hierarchy in a Data-Heavy Interface',
      whatWasHappening: 'The interface needed to present reviews, sentiment indicators, and insights without overwhelming users. The challenge was to prioritize information so users could quickly scan, interpret, and act on key data points.',
      keyFindings: [
        'Data-heavy screens risked visual overload without clear prioritization',
        'Important actions and insights competed with secondary information',
        'Without strong hierarchy, users had to spend extra effort parsing content',
      ],
      whyItMattered: 'Without clear visual hierarchy, users struggled to efficiently process information and take action, reducing the platform\'s effectiveness and user satisfaction.',
      designFocus: [
        'Establish a clear typographic scale to guide attention',
        'Use spacing and visual grouping to reduce cognitive load',
        'Highlight key metrics and actions while deprioritizing secondary details',
        'Design layouts that support fast scanning across devices',
      ],
    },
  ],
  results: {
    title: 'Results',
    description: 'The final product design translates a complex, AI-driven workflow into a clear and approachable experience. By emphasizing visual clarity, hierarchy, and transparency, Inform Reviews enables users to quickly understand the product\'s value, trust automated responses, and act on review insights with confidence. The design establishes a scalable foundation for future features while keeping the experience focused, readable, and easy to use.',
    finalDesignImages: [
      '/inform/Inform - Dashboard.png',
      '/inform/Inform - Weekly Summary.png',
      '/inform/Inform - Landing.png',
      '/inform/Inform - Blog.png',
      '/inform/Inform - Membership.png',
    ],
  },
};
